// ── SalesAccountPage — account detail with Overview / Opportunities / Contacts ──
// Mirrors the portfolio AccountPage tab structure, formatting, and color scheme.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, Icon, STh, SectionHeader, Spinner, StatCard, TabBtn, Td, TdNum, Th, ThNum } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { NotesPanel } from "../../../shared/NotesPanel";
import { EmailActivityPanel } from "../../../shared/EmailActivityPanel";

export const SalesAccountPage = () => {
  const { api, role, view, go, tab, setTab, setModal, setConfirm, delRec, slim } = useApp();
  const [account, setAccount] = useState(null);
  const [opps, setOpps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const acctId = view.accountid;

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("getSalesAccounts", { accountid: acctId }),
      api.get("getOpportunities", { accountid: acctId }),
      api.get("getContacts", { accountid: acctId }),
    ]).then(([a, o, c]) => {
      setAccount((Array.isArray(a) ? a[0] : a) || null);
      setOpps(Array.isArray(o) ? o : []);
      setContacts(Array.isArray(c) ? c : []);
      setLoading(false);
    }).catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [acctId]);

  const { sorted: sortedOpps, sortKey: oKey, sortDir: oDir, toggle: oToggle } = useSortable(opps, 'stage_order', 'asc');
  const { sorted: sortedContacts, sortKey: cKey, sortDir: cDir, toggle: cToggle } = useSortable(contacts, 'lastname', 'asc');

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  if (!account) return <ErrorMsg msg="Account not found." />;

  // Edit uses the FULL account row (getAccount) — getSalesAccounts returns a
  // subset, and saveAccount would null the columns it doesn't send.
  const openEdit = () => {
    api.get("getAccount", { id: account.id })
      .then(full => setModal({ type: "account-edit", rec: (Array.isArray(full) ? full[0] : full) || account, onDone: load }))
      .catch(() => setModal({ type: "account-edit", rec: account, onDone: load }));
  };

  const weighted = (x) => parseFloat(x.commitment_amount || 0) * (parseFloat(x.probability != null ? x.probability : x.stage_probability || 0) / 100);
  const openOpps = opps.filter(o => !o.is_closed);
  const pipelineTotal = openOpps.reduce((s, x) => s + parseFloat(x.commitment_amount || 0), 0);

  const Row = ({ label, value }) => (
    <div style={{ display:"flex", justifyContent:"space-between", gap:8, fontSize:13, padding:"4px 0" }}>
      <span style={{ color:"var(--color-text-secondary)", flexShrink:0 }}>{label}</span>
      <span style={{ textAlign:"right" }}>{value}</span>
    </div>
  );

  const TABS = ["overview", "opportunities", "contacts", "email", "notes"];
  const tabLabel = t => t === "overview" ? "Overview" : t === "opportunities" ? "Opportunities"
    : t === "contacts" ? "Contacts" : t === "email" ? "Email" : "Notes";
  const activeTab = TABS.includes(tab) ? tab : "overview";

  const afterDelete = () => {
    if (!slim) { go("sales-accounts", {}); return; }
    try {
      if (window.opener && !window.opener.closed)
        window.opener.postMessage({ type: "ace:records-changed", table: "Accounts", id: account.id }, window.location.origin);
    } catch (e) { /* opener gone or cross-origin: nothing to tell */ }
    window.close();
    setTimeout(() => { if (!window.closed) go("sales-accounts", {}); }, 300);
  };

  return <>
    <SectionHeader title={account.companyname} actions={[
      role.canEdit ? <Btn key="edit" variant="primary" onClick={openEdit}>Edit</Btn> : null,
      // canDelete is the Admin role and only the Admin role (ROLE_PERMS in
      // shared/auth.js), so this is the admins-only control it reads as.
      //
      // onDone leaves for the accounts list rather than reloading: this page is
      // about to stop existing, and reloading it renders "not found" over a
      // delete that worked.
      //
      // In a slim (popup) window there is no list to go back to: tell the
      // window that opened it (so its Accounts list drops the row) and close.
      // window.close() only works on a window a script opened — slim windows
      // are — but if the browser refuses, fall back to the list.
      //
      // deleteRecord refuses while contacts, opportunities, loans or notes hang
      // off the account, and says which — so the usual outcome here is a
      // sentence explaining what to clear first, not a vanished borrower.
      role.canDelete ? <Btn key="del" variant="danger" onClick={() => setConfirm({
        msg: 'Delete "' + (account.companyname || "this account") + '"?',
        onConfirm: () => delRec("Accounts", account.id,
                                "Deleted account " + (account.companyname || account.id),
                                () => afterDelete()) })}>Delete Account</Btn> : null
    ].filter(Boolean)} />

    {/* ── Header card ── */}
    <div>
      <Card>
        <div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
          <div style={{ minWidth:260 }}>
            <Row label="Company" value={account.companyname} />
            <Row label="Account #" value={<span style={{ fontFamily:"var(--font-mono)", fontSize:12 }}>{account.accountnumber || "—"}</span>} />
            <Row label="Lifecycle Stage" value={<Badge s={account.lifecycle_stage} />} />
            <Row label="Sales Owner" value={account.sales_owner_name || "—"} />
            <Row label="Lead Source" value={account.lead_source || "—"} />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3, minmax(120px, 1fr))", gap:12, flex:1 }}>
            <StatCard label="Open Opportunities" value={openOpps.length} />
            <StatCard label="Open Pipeline" value={fmt(pipelineTotal)} />
            <StatCard label="Contacts" value={contacts.length} />
          </div>
        </div>
      </Card>
    </div>

    {/* ── Tabs (same structure/formatting/colors as the portfolio account page) ── */}
    <div style={{ marginTop:16 }}>
      <div style={{ display:"flex", flexWrap:"wrap", gap:3, paddingLeft:0, position:"relative", zIndex:1, marginTop:4, borderBottom:"4px solid "+B.cyan }}>
        {TABS.map(t => <TabBtn key={t} id={t} current={activeTab} label={tabLabel(t)} onClick={setTab} />)}
      </div>
      <div style={{ background:"var(--color-background-primary)", border:"0.5px solid var(--color-border-tertiary)", borderTop:"none", borderRadius:"0 0 8px 8px", padding:16, position:"relative", zIndex:2 }}>

        {activeTab === "overview" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 32px", maxWidth:720 }}>
            <Row label="Company" value={account.companyname} />
            <Row label="Account #" value={account.accountnumber || "—"} />
            <Row label="Stage" value={<Badge s={account.lifecycle_stage} />} />
            <Row label="Visible to PMs" value={account.portfolio_visible ? "Yes" : "No"} />
            <Row label="Sales Owner" value={account.sales_owner_name || "—"} />
            <Row label="Lead Source" value={account.lead_source || "—"} />
            <Row label="Phone" value={account.companyphone || "—"} />
            <Row label="Email" value={account.companyemail || "—"} />
            <Row label="City / State" value={[account.city, account.state].filter(Boolean).join(", ") || "—"} />
            <Row label="Website" value={account.website || "—"} />
          </div>
        )}

        {activeTab === "opportunities" && <>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
            {role.canEdit && <Btn variant="primary" onClick={() => setModal({ type:"opp-add", accountid:account.id, onDone:load })}>+ Add Opportunity</Btn>}
          </div>
          {opps.length === 0
            ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"8px 0" }}>No opportunities for this account.</div>
            : <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                <thead><tr>
                  <STh label="Opportunity" sortKey="name"                currentKey={oKey} currentDir={oDir} onSort={oToggle} />
                  <STh label="Stage"       sortKey="stage_order"         currentKey={oKey} currentDir={oDir} onSort={oToggle} />
                  <STh label="Owner"       sortKey="ownerName"           currentKey={oKey} currentDir={oDir} onSort={oToggle} />
                  <STh label="Commitment"  sortKey="commitment_amount"   currentKey={oKey} currentDir={oDir} onSort={oToggle} numeric />
                  <STh label="Weighted"    sortKey="probability"         currentKey={oKey} currentDir={oDir} onSort={oToggle} numeric />
                  <STh label="Exp Close"   sortKey="expected_close_date" currentKey={oKey} currentDir={oDir} onSort={oToggle} />
                  <Th></Th>
                </tr></thead>
                <tbody>{sortedOpps.map(o => (
                  <tr key={o.id} style={{ cursor:"pointer" }} onClick={() => setModal({ type: "opp-view", rec:o, accountid:account.id, onDone:load })}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <Td style={{ fontWeight:500 }}>{o.name || "—"}</Td>
                    <Td>{o.stage || "—"}</Td>
                    <Td>{o.ownerName || "—"}</Td>
                    <TdNum>{o.commitment_amount != null ? fmt(o.commitment_amount) : "—"}</TdNum>
                    <TdNum>{fmt(weighted(o))}</TdNum>
                    <Td>{o.expected_close_date ? fmtDate(o.expected_close_date) : "—"}</Td>
                    <Td><div style={{ display:"flex", gap:6, justifyContent:"flex-end" }} onClick={e => e.stopPropagation()}>
                      <Btn onClick={() => setModal({ type: "opp-view", rec:o, accountid:account.id, onDone:load })}><Icon type="view" /></Btn>
                      {role.canEdit && <Btn onClick={() => setModal({ type:"opp-edit", rec:o, accountid:account.id, onDone:load })}><Icon type="edit" /></Btn>}
                      {role.canDelete && <Btn onClick={() => setConfirm({ msg:"Delete this opportunity?", onConfirm:() => delRec("Opportunities", o.id, "Deleted opportunity", load) })} variant="danger"><Icon type="delete" /></Btn>}
                    </div></Td>
                  </tr>
                ))}</tbody>
              </table>}
        </>}

        {activeTab === "contacts" && <>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
            {role.canEdit && <Btn variant="primary" onClick={() => setModal({ type:"contact-add", accountid:account.id, onDone:load })}>+ Add Contact</Btn>}
          </div>
          {contacts.length === 0
            ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"8px 0" }}>No contacts for this account.</div>
            : <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                <thead><tr>
                  <STh label="Name"   sortKey="lastname"    currentKey={cKey} currentDir={cDir} onSort={cToggle} />
                  <STh label="Title"  sortKey="title"       currentKey={cKey} currentDir={cDir} onSort={cToggle} />
                  <STh label="Email"  sortKey="email"       currentKey={cKey} currentDir={cDir} onSort={cToggle} />
                  <STh label="Phone"  sortKey="phone"       currentKey={cKey} currentDir={cDir} onSort={cToggle} />
                  <STh label="Mobile" sortKey="mobilephone" currentKey={cKey} currentDir={cDir} onSort={cToggle} />
                  <Th></Th>
                </tr></thead>
                <tbody>{sortedContacts.map(c => (
                  <tr key={c.Id || c.id} style={{ cursor:"pointer" }} onClick={() => setModal({ type: "contact-view", rec:c, onDone:load })}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <Td style={{ fontWeight:500 }}>{[c.firstname, c.lastname].filter(Boolean).join(" ") || "—"}</Td>
                    <Td>{c.title || "—"}</Td>
                    <Td>{c.email || "—"}</Td>
                    <Td>{c.phone || "—"}</Td>
                    <Td>{c.mobilephone || "—"}</Td>
                    <Td><div style={{ display:"flex", gap:6, justifyContent:"flex-end" }} onClick={e => e.stopPropagation()}>
                      <Btn onClick={() => setModal({ type: "contact-view", rec:c, onDone:load })}><Icon type="view" /></Btn>
                      {role.canEdit && <Btn onClick={() => setModal({ type:"contact-edit", rec:c, onDone:load })}><Icon type="edit" /></Btn>}
                      {role.canDelete && <Btn onClick={() => setConfirm({ msg:"Delete this contact?", onConfirm:() => delRec("Contacts", c.Id || c.id, "Deleted contact", load) })} variant="danger"><Icon type="delete" /></Btn>}
                    </div></Td>
                  </tr>
                ))}</tbody>
              </table>}
        </>}

        {activeTab === "email" && (
          <div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
              External email captured from Microsoft 365 for this account — including mail to and from its contacts.
              Internal-only threads are never captured.
            </div>
            <EmailActivityPanel accountid={account.id} rollup />
          </div>
        )}

        {activeTab === "notes" && (
          <div>
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 10 }}>
              All notes for this account — including notes logged on its opportunities and contacts.
            </div>
            <NotesPanel entitytype="Account" entityid={account.id} accountid={account.id} rollup />
          </div>
        )}

      </div>
    </div>
  </>;
};
